import { useTranslations } from "next-intl";
import React from "react";

interface PaginationProps {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalCount,
  pageSize,
  onPageChange,
}) => {
  const t = useTranslations("Paginator");
  const totalPages = Math.ceil(totalCount / pageSize);
  if (totalPages <= 1) return null;

  const getVisiblePages = (): (number | null)[] => {
    const delta = 1;
    const range: number[] = [];
    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    const pages: (number | null)[] = [1];
    if (range[0] > 2) pages.push(null);
    pages.push(...range);
    if (range[range.length - 1] < totalPages - 1) pages.push(null);
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  return (
    <nav className="flex justify-center mt-8">
      <ul className="inline-flex items-center gap-2 flex-wrap justify-center">
        <li className="sm:hidden">
          <button
            type="button"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className="h-10 w-10 bg-white border rounded-lg border-gray-3 text-dark disabled:opacity-50"
          >
            {"<<"}
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="h-10 w-10 sm:w-auto sm:px-3 py-1 bg-white border rounded-lg border-gray-3 text-dark disabled:opacity-50"
          >
            <span className="sm:hidden">{"<"}</span>
            <span className="hidden sm:inline">{t("Prev")}</span>
          </button>
        </li>
        {getVisiblePages().map((page, index) =>
          page === null ? (
            <li key={`ellipsis-${index}`} className="hidden sm:block">
              <span className="px-1 text-dark select-none">…</span>
            </li>
          ) : (
            <li
              key={page}
              className={page === currentPage ? "block" : "hidden sm:block"}
            >
              <button
                type="button"
                onClick={() => onPageChange(page)}
                className={`px-3 py-1 h-10 w-10 rounded-lg border border-gray-3 ${
                  page === currentPage
                    ? "bg-blue text-white border-blue"
                    : "bg-white text-dark border-gray-3"
                }`}
              >
                {page}
              </button>
            </li>
          )
        )}
        <li>
          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="h-10 w-10 sm:w-auto sm:px-3 py-1 bg-white border rounded-lg border-gray-3 text-dark disabled:opacity-50"
          >
            <span className="sm:hidden">{">"}</span>
            <span className="hidden sm:inline">{t("Next")}</span>
          </button>
        </li>
        <li className="sm:hidden">
          <button
            type="button"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="h-10 w-10 bg-white border rounded-lg border-gray-3 text-dark disabled:opacity-50"
          >
            {">>"}
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default Pagination;
